import { ReebeloConditions } from '@lambda/reebelo';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { RecentlyViewedProductT } from '@lambda/apis/src/dynamoDb/types';
import { handleFreeReturn } from '@lambda/commons/helpers';
import dayjs from 'dayjs';

// Types
import {
  ProductPageProps,
  selectedSkuDetailsT,
  useProductPageResult,
} from './types';

// Helpers
import { generateSavingsPercentage, VariantTree } from './helpers';
import { generateCarouselImages } from '../sections/main/left/image/helpers';
import { unpackAvailableVariants } from './variantPacking';

// Custom Hooks
import { useRecentlyViewed } from '@/lib/use-recently-viewed';
import useVendorDetails from './useVendorDetails';
import useStreamlineUrl from './useStreamlineUrl';
import useSelectedSku from './useSelectedSku';
import useAddons from './useAddons';

// Settings
import settings from '@/settings';

const { store, free_return_days } = settings;

const useProductPage = (value: ProductPageProps): useProductPageResult => {
  const router = useRouter();
  const { productId, product } = value;

  // The page ships these packed to keep __NEXT_DATA__ small; expand once here
  // so no consumer has to care.
  const availableVariants = useMemo(
    () => unpackAvailableVariants(value.availableVariants),
    [value.availableVariants],
  );

  const isOutOfStock = availableVariants.length === 0;

  // SKU ID from URL or Fallback to Valid SKU ID
  const skuId = useMemo<string | null>(
    () => (router.query?.skuId as string | undefined) ?? value.skuId,
    [router.query?.skuId, value.skuId],
  );

  // Replace URL with the Valid SKU ID
  useStreamlineUrl({ productId, skuId: value.skuId });

  // Selected Variant
  const selectedVariant = useMemo(() => {
    const matchingSku = availableVariants.find((s) => s.skuId === skuId);

    if (skuId && matchingSku) {
      return matchingSku;
    }

    return null;
  }, [availableVariants, skuId]);

  // Fetch Selected SKU on SKU ID Change
  const { sku: selectedSku, isFetchingSku } = useSelectedSku(
    skuId,
    value.selectedSku,
  );

  // Selected Offer (cheapest offer of the selected SKU)
  const selectedOffer = useMemo(() => {
    if (!selectedSku) {
      return null;
    }

    const matchingOffer = selectedSku?.offers.find(
      (offer) => offer.id === selectedVariant?.offerId,
    );

    const cheapestOffer = selectedSku?.offers?.[0];

    // Find offer matching the selected variant's offer ID, else return the cheapest offer
    return matchingOffer ?? cheapestOffer ?? null;
  }, [selectedSku, selectedVariant]);

  // Conditions are keyed by reebeloId; vendor details are still pk-based.
  const categoryId = product.category?.id ?? '';
  const categoryPk = product.category?.pk ?? '';

  // Category Name
  const categoryName = product.category?.name ?? '';

  // Price in Cents
  const price = selectedOffer?.price ?? 0;

  // Fetch Vendor Details on Selected Offer Change
  const vendorDetails = useVendorDetails({
    vendorName: selectedOffer?.vendor.id?.split('#')?.[1] ?? null,
    categoryPk,
    price,
  });

  // Add-ons: Warranty
  const { selectedWarranty, isWarrantySelected, setWarrantySelected } =
    useAddons({
      warranty: vendorDetails.warranty,
    });

  // Streamline Data

  const selectedSkuDetails: selectedSkuDetailsT = useMemo(() => {
    // Use Selected SKU Title if exists otherwise use Product Title
    const title = selectedSku?.title ?? product.title;

    // Compare at Price in Dollars
    const compareAtPrice = selectedSku?.compareAtPrice
      ? selectedSku.compareAtPrice / 100
      : null;

    // Final Price in Dollars
    const finalPrice = price / 100 + (selectedWarranty?.price ?? 0);

    // Condition
    const condition =
      selectedVariant?.variants.find((v) => v.variant.slug === 'condition')
        ?.value?.value ?? ReebeloConditions.Acceptable;

    const carrier =
      selectedVariant?.variants.find((v) => v.variant.slug === 'carrier')?.value
        ?.value ?? null;

    // Brand Name
    const brandName = product?.brand?.name ?? '';

    return {
      title,
      price: price / 100, // Price in Dollars
      finalPrice,
      compareAtPrice,
      savingsPercentage: generateSavingsPercentage(price / 100, compareAtPrice),
      freeReturnDays: handleFreeReturn(store, free_return_days),
      categoryName,
      categoryId,
      condition,
      brandName,
      carrier,
    };
  }, [selectedSku, selectedVariant, selectedWarranty]);

  const variantTree = useMemo(
    () => new VariantTree(availableVariants, product.variants ?? []),
    [availableVariants, product.variants],
  );

  // Recenently Viewed Products // TODO: Refactor this
  const toBeAddedToRecentlyViewed: RecentlyViewedProductT | undefined =
    selectedOffer
      ? {
          name: selectedSkuDetails.title,
          image:
            generateCarouselImages(selectedSku, product, isOutOfStock)?.[0]
              ?.url ?? '',
          type: 'product-v2',
          handle: `${productId}${
            selectedVariant ? `?skuId=${selectedVariant.skuId}` : ''
          }`,
          sku: selectedVariant?.skuId ?? '',
          tags: [],
          addedAt: dayjs().format(),
          price: selectedSkuDetails.price,
          comparePrice: selectedSkuDetails.compareAtPrice,
          summary: value.additionalInfo.reviews?.summary ?? null,
          vendor: selectedOffer?.vendor.name ?? vendorDetails.name ?? '',
          isCheapest: availableVariants.every(
            (s) => (selectedOffer?.price ?? Infinity) <= s.price,
          ),
          featured: selectedOffer?.featured || false,
        }
      : undefined;

  const [recentlyViewed, fetchingRecentlyViewed] = useRecentlyViewed(
    toBeAddedToRecentlyViewed,
  );

  const props: useProductPageResult = {
    ...value,
    availableVariants,
    selectedSku,
    selectedSkuDetails,
    selectedVariant,
    selectedOffer,
    variantTree,
    selectedWarranty,
    isWarrantySelected,
    setWarrantySelected,
    vendorDetails,
    recentlyViewed: {
      products: recentlyViewed as RecentlyViewedProductT[],
      fetching: fetchingRecentlyViewed as boolean,
    },
    isFetchingSku,
    isOutOfStock,
  };

  return props;
};

export default useProductPage;
